import { useRef, useState, useCallback } from 'react';
import { UploadCloud, X, ImageIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

type TProps = {
	onChange: (files: FileList | null) => void;
	error?: boolean;
	aspectRatio?: 'square' | 'banner'; // square = 1:1, banner = 16:9 ou similaire
};

const ImageUploadInput = ({ onChange, error, aspectRatio = 'square' }: TProps) => {
	const inputRef = useRef<HTMLInputElement>(null);
	const [preview, setPreview] = useState<string | null>(null);
	const [isDragging, setIsDragging] = useState(false);

	const handleFile = (file: File | null) => {
		if (!file) return;
		const dt = new DataTransfer();
		dt.items.add(file);
		onChange(dt.files);
		const reader = new FileReader();
		reader.onload = e => setPreview(e.target?.result as string);
		reader.readAsDataURL(file);
	};

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		handleFile(e.target.files?.[0] ?? null);
	};

	const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
		e.preventDefault();
		setIsDragging(false);
		const file = e.dataTransfer.files?.[0];
		if (file && file.type.startsWith('image/')) handleFile(file);
	}, []);

	const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
		e.preventDefault();
		setIsDragging(true);
	};

	const handleDragLeave = () => setIsDragging(false);

	const handleRemove = (e: React.MouseEvent) => {
		e.stopPropagation();
		setPreview(null);
		onChange(null);
		if (inputRef.current) inputRef.current.value = '';
	};

	return (
		<div
			onClick={() => !preview && inputRef.current?.click()}
			onDrop={handleDrop}
			onDragOver={handleDragOver}
			onDragLeave={handleDragLeave}
			className={cn(
				'relative w-full h-fit overflow-hidden rounded-lg border-2 border-dashed transition-colors duration-150',
				aspectRatio === 'square' ? 'aspect-square' : 'aspect-video',
				!preview && 'cursor-pointer hover:border-primary/60 hover:bg-muted/40',
				isDragging && 'border-primary bg-primary/5',
				error ? 'border-red-400' : 'border-muted-foreground/25',
				preview ? 'border-solid border-muted' : ''
			)}
		>
			<input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={handleInputChange} />

			{preview ? (
				<div className="relative group h-full w-full">
					<img src={preview} alt="preview" className="w-full h-full object-cover rounded-lg" />
					<div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center gap-3">
						<button
							type="button"
							onClick={() => inputRef.current?.click()}
							className="text-white text-xs bg-white/20 hover:bg-white/30 backdrop-blur-xs px-3 py-1.5 rounded-md transition-colors"
						>
							Changer
						</button>
						<button
							type="button"
							onClick={handleRemove}
							className="text-white bg-red-500/80 hover:bg-red-500 p-1.5 rounded-md transition-colors"
						>
							<X className="h-3.5 w-3.5" />
						</button>
					</div>
				</div>
			) : (
				<div className="flex flex-col items-center justify-center gap-2 h-full py-8 px-4 text-center">
					<div className={cn('rounded-full p-3 transition-colors', isDragging ? 'bg-primary/10' : 'bg-muted')}>
						{isDragging ? (
							<UploadCloud className="h-6 w-6 text-primary" />
						) : (
							<ImageIcon className="h-6 w-6 text-muted-foreground" />
						)}
					</div>
					<div>
						<p className="text-sm font-medium text-foreground">{isDragging ? 'Déposer ici' : 'Glissez une image ou cliquez'}</p>
						<p className="text-xs text-muted-foreground mt-0.5">PNG, JPG, WEBP — max 10 Mo</p>
					</div>
				</div>
			)}
		</div>
	);
};

export default ImageUploadInput;
